import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams, Link } from 'react-router-dom'
import { api, type Lookup, type WorkOrder, type WorkOrderItem, type WorkOrderArticle } from '../lib/api'
import { money, currentPeriod, today } from '../lib/format'
import { defaultTerms, isDefaultTerms } from '../lib/terms'
import { Panel, Field, Alert, Loading, Tag } from '../components/ui'
import Combo from '../components/Combo'
import { MonthPicker } from '../components/MonthRangePicker'

type ItemDraft = Omit<WorkOrderItem, 'subtotal'> & { subtotal: number }

/** 網址帶進來的月份要驗過再用，格式不對就當沒帶（後端也會再擋一次） */
function validPeriod(v: string | null): string | null {
  return v && /^\d{4}-(0[1-9]|1[0-2])$/.test(v) ? v : null
}

/**
 * 換發包類型時一併處理注意事項：目前條款若還是某一種預設，就換成新類型的預設；
 * 已經手動改過的內容原封不動，不能因為切個下拉就把使用者打的字洗掉。
 */
function pickDispatchType<T extends { dispatch_type: string; terms: string }>(h: T, next: string): T {
  return {
    ...h,
    dispatch_type: next,
    terms: isDefaultTerms(h.terms) ? defaultTerms(next) : h.terms,
  }
}

/** 發包單位固定是我們自己；後端也有同樣的預設，兩邊都補才不會有漏網的單 */
const DEFAULT_DEPT = '資訊部 ／ 文案組'

/** 唯一可自由編輯的狀態；送出後就進入「待確認」並鎖定 */
const DRAFT_STATUS = '待補充'

/** 個人寫手沒有發票，款項不另計稅；列印表的合計區也會換成不含稅的寫法 */
const ACTUAL_PAY = '實際支付'

const blankItem = (seq: number): ItemDraft => ({
  seq,
  client_id: 0,
  contract_code: null,
  job_type_id: null,
  owner_id: null,
  service: null,
  qty: 1,
  unit_price: 0,
  subtotal: 0,
})

const blankArticle = (seq: number): WorkOrderArticle => ({
  seq,
  item_id: null,
  client_id: null,
  title: '',
  keyword: '',
  word_target: 1200,
  due_on: null,
})

export default function WorkOrderForm() {
  const { id } = useParams()
  const nav = useNavigate()
  const location = useLocation()
  const fromEval = (location.state as any)?.evaluation

  const [lookup, setLookup] = useState<Lookup | null>(null)
  const [loaded, setLoaded] = useState(!id)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [existing, setExisting] = useState<WorkOrder | null>(null)

  const [header, setHeader] = useState({
    // 新增時由列表頁的對話框帶月份進來（?period=YYYY-MM）；沒帶就用當月
    period: validPeriod(new URLSearchParams(location.search).get('period')) ?? currentPeriod(),
    dispatch_type: '外包',
    writer_id: 0,
    issuer_id: null as number | null,
    issuer_contact: '',
    owner_id: null as number | null,
    dept: DEFAULT_DEPT,
    issued_on: today(),
    confirmed_on: '',
    due_on: '',
    lang: '英',
    tax_mode: 'NTD／未稅',
    rate_level: '',
    deliver_link: '',
    job_type_id: null as number | null,
    note: '',
    terms: defaultTerms('外包'),
  })
  const [items, setItems] = useState<ItemDraft[]>([blankItem(1)])
  const [articles, setArticles] = useState<WorkOrderArticle[]>([blankArticle(1)])
  const [role, setRole] = useState('')
  /**
   * 管理員按下「解鎖編輯」後開放編輯。狀態會一起退回「待補充」，
   * 這個旗標只是在等後端回應的空檔先把欄位打開，免得畫面卡一下才能改。
   */
  const [unlocked, setUnlocked] = useState(false)

  /**
   * 補填舊單時要照紙本上的號填，不能另給新號，所以建立前可以切換單號來源。
   * 只在「還沒建立」時有意義：存過檔之後單號就固定了。
   */
  const [codeMode, setCodeMode] = useState<'auto' | 'manual'>('auto')
  const [manualCode, setManualCode] = useState('')

  /* -------- 載入下拉資料 -------- */
  useEffect(() => {
    api.get<Lookup>('/masters/lookup').then(setLookup).catch((e) => setError(e.message))
    api.get<{ user: { role: string } }>('/me').then((d) => setRole(d.user.role)).catch(() => {})
  }, [])

  /**
   * 欄位可不可以改：
   *   - 新增中的單（還沒存過）一律可改
   *   - 「待補充」是唯一可自由編輯的狀態
   *   - 送出之後鎖定，只有管理員按「解鎖編輯」才會開放
   */
  const isDraft = !existing || existing.status === DRAFT_STATUS
  const editable = isDraft || unlocked
  const isAdmin = role === 'admin'

  /* -------- 編輯既有單 -------- */
  useEffect(() => {
    if (!id) return
    api
      .get<WorkOrder>(`/work-orders/${id}`)
      .then((w) => {
        setExisting(w)
        setHeader({
          period: w.period,
          dispatch_type: w.dispatch_type ?? '外包',
          writer_id: w.writer_id,
          issuer_id: w.issuer_id,
          // 舊資料沒存過就用寫手主檔的 Email 遞補，跟列印表顯示的值一致
          issuer_contact: w.issuer_contact || w.writer_email || '',
          owner_id: w.owner_id,
          // 舊資料的發包單位是空的，開啟時就補上，存檔後就正式寫回去
          dept: w.dept?.trim() || DEFAULT_DEPT,
          issued_on: w.issued_on ?? '',
          confirmed_on: w.confirmed_on ?? '',
          due_on: w.due_on ?? '',
          lang: w.lang ?? '英',
          tax_mode: w.tax_mode,
          rate_level: w.rate_level ?? '',
          deliver_link: w.deliver_link ?? '',
          job_type_id: w.job_type_id,
          note: w.note ?? '',
          // 舊單沒存過條款，帶出該類型的預設讓使用者直接看到並可修改
          terms: w.terms?.trim() || defaultTerms(w.dispatch_type),
        })
        setItems(w.items.length ? (w.items as ItemDraft[]) : [blankItem(1)])
        setArticles(w.articles.length ? w.articles : [blankArticle(1)])
        setLoaded(true)
      })
      .catch((e) => { setError(e.message); setLoaded(true) })
  }, [id])

  /* -------- 由評估表帶入 -------- */
  useEffect(() => {
    if (!fromEval || id) return
    setHeader((h) => ({
      ...h,
      period: fromEval.period ?? h.period,
      writer_id: fromEval.writer_id ?? 0,
      owner_id: fromEval.owner_id ?? null,
      due_on: fromEval.due_on ?? '',
    }))
    setItems([
      {
        ...blankItem(1),
        client_id: fromEval.client_id,
        contract_code: fromEval.contract_code,
        qty: fromEval.qty,
        unit_price: fromEval.unit_price,
        subtotal: fromEval.qty * fromEval.unit_price,
      },
    ])
  }, [fromEval, id])

  /* -------- 選承包人自動帶語系／單價／稅別 -------- */
  const pickWriter = (wid: number) => {
    const w = lookup?.writers.find((x) => x.id === wid)
    setHeader((h) => ({
      ...h,
      writer_id: wid,
      lang: w?.lang ?? h.lang,
      issuer_contact: w?.email ?? h.issuer_contact,
      // 只有廠商會開發票、要計 5% 營業稅；個人與內部發包都是實際支付
      tax_mode: w ? (w.kind === '廠商' ? 'NTD／未稅' : ACTUAL_PAY) : h.tax_mode,
      // 內部發包的寫手就是自家人，發包類型直接帶「內包」（仍可手動改回）。
      // 這裡只能挑出要改的欄位：pickDispatchType 回傳的是整包 header 的複本，
      // 直接展開會把上面剛設好的 writer_id／lang／tax_mode 又蓋回舊值。
      ...(w?.kind === '內部發包'
        ? {
            dispatch_type: '內包',
            terms: isDefaultTerms(h.terms) ? defaultTerms('內包') : h.terms,
          }
        : null),
    }))
    if (w) {
      setItems((list) =>
        list.map((it) =>
          it.unit_price === 0
            ? { ...it, unit_price: w.default_price, subtotal: it.qty * w.default_price }
            : it
        )
      )
    }
  }

  const setItem = (idx: number, patch: Partial<ItemDraft>) => {
    setItems((list) =>
      list.map((it, i) => {
        if (i !== idx) return it
        const next = { ...it, ...patch }
        next.subtotal = (next.qty || 0) * (next.unit_price || 0)
        return next
      })
    )
  }

  const totals = useMemo(
    () => ({
      qty: items.reduce((n, i) => n + (i.qty || 0), 0),
      amount: items.reduce((n, i) => n + (i.subtotal || 0), 0),
    }),
    [items]
  )

  /**
   * silent：由「送出審核」呼叫時用，存檔成功不另外跳提示，錯誤照樣往外丟
   * review：新增時直接建成「待審核」，省掉「先存草稿再送出」的第二次往返
   */
  const save = async ({ silent = false, review = false } = {}): Promise<WorkOrder | null> => {
    if (!silent) { setError(''); setSaving(true) }
    try {
      if (!header.writer_id) throw new Error('請選擇承包人')
      // 選了「指定單號」卻沒填，會靜靜地改成自動產號，不如直接擋下來講清楚
      if (!id && codeMode === 'manual' && !/^\d{9}$/.test(manualCode.trim())) {
        throw new Error('指定單號要填 9 碼數字（年月＋3 碼流水），例 202506007')
      }
      if (items.some((i) => !i.client_id)) throw new Error('每一列發包明細都要選客戶')

      const payload = {
        ...header,
        items: items.map((i, idx) => ({ ...i, seq: idx + 1 })),
        articles: articles
          .filter((a) => a.title || a.keyword)
          .map((a, idx) => ({ ...a, seq: idx + 1 })),
        evaluation_id: fromEval?.id ?? null,
        submit_for_review: review,
        // 只有新增、且選了「指定單號」時才送；更新既有單不會動到單號
        code: !id && codeMode === 'manual' ? manualCode.trim() : null,
      }
      const res = id
        ? await api.put<WorkOrder>(`/work-orders/${id}`, payload)
        : await api.post<WorkOrder>('/work-orders', payload)
      nav(`/work-orders/${res.id}`, { replace: true })
      setExisting(res)
      if (!silent) {
        setError('')
        alert(
          review
            ? `${res.code} 已送出審核，等待管理員核准`
            : `已儲存草稿，發包單號：${res.code}`
        )
      }
      return res
    } catch (e: any) {
      if (silent) throw e
      setError(e.message)
      return null
    } finally {
      if (!silent) setSaving(false)
    }
  }

  /**
   * 開列印預覽。列印與下載 PDF 都在預覽頁上操作，這裡只負責把人帶過去。
   *
   * 列印頁的網址要 id，新增中的單還沒有，所以先存成草稿再開；
   * 已存在且還可編輯的也先存一次，避免預覽到的是舊內容。
   *
   * 分頁必須在點擊當下同步開啟：等 await 結束再 window.open 會失去使用者手勢，
   * 被瀏覽器當成彈出式廣告擋掉。所以先開空白分頁佔位，存檔完成後再導向。
   */
  const openPrint = async () => {
    const win = window.open('', '_blank')
    setError('')
    setSaving(true)
    let target = existing
    try {
      // 已送出的單是唯讀的，沒東西可存，直接開預覽
      if (editable) target = await save({ silent: true })
    } catch (e: any) {
      setError(e.message)
      target = null
    } finally {
      setSaving(false)
    }
    if (!target) {
      win?.close()          // 存檔失敗（例如漏選承包人）就別留一個空白分頁
      return
    }
    const url = `/work-orders/${target.id}/print`
    if (win) win.location.href = url
    else window.location.href = url    // 分頁被擋掉時退而求其次，直接在本頁開
  }

  const changeStatus = async (status: string) => {
    if (!id) return
    try {
      const res = await api.post<WorkOrder>(`/work-orders/${id}/status`, { status })
      setExisting(res)
    } catch (e: any) {
      setError(e.message)
    }
  }

  /**
   * 解鎖編輯（限管理員）：把狀態退回「待補充」並開放欄位。
   * 狀態一起退回，單子才不會停在「待確認」卻又被改內容——
   * 承包人看到的版本與系統上的狀態會對不起來。改完要重新送出。
   */
  const unlock = async () => {
    if (!existing) return
    const ok = confirm(
      `確定要解鎖發包單 ${existing.code}？\n\n` +
      `狀態會從「${existing.status}」退回「${DRAFT_STATUS}」，欄位開放編輯。\n` +
      `修改完成後需要再送出一次。`
    )
    if (!ok) return
    setUnlocked(true)
    await changeStatus(DRAFT_STATUS)
  }

  /** 送出審核：先把目前的編輯存起來，再把狀態推到「待審核」並鎖定欄位 */
  const submit = async () => {
    if (!existing) return
    const ok = confirm(
      `確定要送出發包單 ${existing.code} 審核？\n\n` +
      `送出後狀態變成「待審核」，所有欄位會鎖定成唯讀。\n` +
      `管理員核准後才會進入「待確認」發給廠商；若被退回會回到「${DRAFT_STATUS}」並附上原因。\n\n` +
      `目前未儲存的變更會一併存檔。`
    )
    if (!ok) return
    setError('')
    setSaving(true)
    try {
      await save({ silent: true })
      const res = await api.post<WorkOrder>(`/work-orders/${existing.id}/status`, { status: '待審核' })
      setExisting(res)
      setUnlocked(false)
      alert(`${res.code} 已送出審核，等待管理員核准`)
    } catch (e: any) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }


  if (!loaded) return <Loading />

  return (
    <>
      <div className="page-head">
        <h2>{existing ? `發包單 ${existing.code}` : '新增發包單（補）'}</h2>
        {existing && <Tag value={existing.status} />}
        {/*
          發包月份只在「還沒建立」時出現：它決定發包單號（2026-08 → 202608012），
          存檔產生單號後就不能改，所以放在建立前的頁首讓人先確認。
          已建立的單標題就看得到單號，不需要再顯示一次。
        */}
        {!existing && (
          <>
            <span className="hint">發包月份</span>
            <MonthPicker
              value={header.period}
              onChange={(v) => setHeader((h) => ({ ...h, period: v }))}
            />
            <span className="hint">單號</span>
            <select
              value={codeMode}
              style={{ width: 110 }}
              onChange={(e) => setCodeMode(e.target.value as 'auto' | 'manual')}
            >
              <option value="auto">自動產生</option>
              <option value="manual">指定單號</option>
            </select>
            {codeMode === 'manual' && (
              <input
                value={manualCode}
                onChange={(e) => setManualCode(e.target.value)}
                placeholder="例 202506007"
                style={{ width: 130 }}
              />
            )}
          </>
        )}
        {fromEval && !id && <span className="hint">由評估表 #{fromEval.id} 轉入</span>}
        {existing && !editable && (
          <span className="hint">
            已送出，欄位唯讀{isAdmin ? `（可按「解鎖編輯」退回「${DRAFT_STATUS}」修改）` : '，如需修改請找管理員'}
          </span>
        )}
        {existing && !isDraft && unlocked && <span className="hint">管理員編輯中</span>}
        <div className="spacer" />
        <div className="btn-row">
          {/* 新增中的單也能預覽：按下去會先存成草稿拿到單號，再開列印頁 */}
          <button onClick={() => openPrint()} disabled={saving}>列印預覽</button>
          {existing && (
            <>
              <select
                value={existing.status}
                onChange={(e) => changeStatus(e.target.value)}
                style={{ width: 110 }}
              >
                {/* 送出後就不再提供「待補充」，避免退回草稿 */}
                {(isDraft ? [DRAFT_STATUS] : []).concat(['待確認', '待交稿', '已交稿', '驗收中', '已完成', '終止']).map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </>
          )}
          {existing && !isDraft && !unlocked && isAdmin && (
            <button title={`把狀態退回「${DRAFT_STATUS}」並開放編輯，改完要再送出一次`} onClick={unlock}>
              解鎖編輯
            </button>
          )}
          {/* 新增時不放「返回清單」：這一頁只有「存草稿」與「送出審核」兩條出路 */}
          {existing && <Link to="/work-orders"><button>返回清單</button></Link>}
          {editable && (
            <button onClick={() => save()} disabled={saving}>
              {saving ? '儲存中…' : existing ? '儲存變更' : '儲存草稿'}
            </button>
          )}
          {/* 新增：一次建立並送審。既有草稿：先存檔再送審 */}
          {!existing && (
            <button className="primary" onClick={() => save({ review: true })} disabled={saving}>
              {saving ? '處理中…' : '送出審核'}
            </button>
          )}
          {existing && isDraft && (
            <button className="primary" onClick={submit} disabled={saving}>
              {saving ? '處理中…' : '送出審核'}
            </button>
          )}
        </div>
      </div>

      <Alert kind="error">{error}</Alert>

      {/* 被審核退回的單會停在「待補充」並帶著原因，不講清楚對方不知道要改什麼 */}
      {existing && isDraft && existing.review_note && (
        <Alert kind="warn">
          此發包單於 {existing.reviewed_at} 被 {existing.reviewed_by} 退回：{existing.review_note}
          　修改後請再次「送出審核」。
        </Alert>
      )}

      {/* fieldset[disabled] 會一次停用底下所有輸入框、下拉與按鈕，
          比逐一加 disabled 可靠，不會有漏改的欄位 */}
      <fieldset className="form-lock" disabled={!editable}>
      <Panel title="一、基本資料">
        {/* 12 欄網格：日期、語系這類短欄位不必佔滿一格，
            原本的三列四欄加最後一列連結（四排）因此收成三排 */}
        <div className="grid c12">
          {/* 申請月份不再放在這裡編輯：它決定發包單號（202608009 已經帶著月份），
              改了會跟單號對不起來。標題列的單號就看得到月份 */}
          <Field label="發包類型" span={3}>
            <select
              value={header.dispatch_type ?? '外包'}
              onChange={(e) => setHeader((h) => pickDispatchType(h, e.target.value))}
            >
              <option value="外包">外包</option>
              <option value="內包">內包</option>
            </select>
          </Field>
          <Field label="承包人（寫手）" span={3}>
            {/* 寫手名單會一直長，改成打字搜尋：打「吳」就補成「吳佩宣」，
                英文名（Sandy、Ed）也找得到；語系與單價放在建議清單裡看 */}
            <Combo
              items={lookup?.writers ?? []}
              value={header.writer_id || null}
              placeholder="輸入寫手姓名"
              allowEmpty={false}
              search={(w) => `${w.name} ${w.name_en ?? ''}`}
              sub={(w) => `[${w.lang}] ${money(w.default_price)}`}
              onChange={(w) => pickWriter(w ? w.id : 0)}
            />
          </Field>
          <Field label="發包單位" span={3}>
            <input value={header.dept} onChange={(e) => setHeader({ ...header, dept: e.target.value })} />
          </Field>
          <Field label="案件負責人" span={3}>
            <select value={header.owner_id ?? ''} onChange={(e) => setHeader({ ...header, owner_id: Number(e.target.value) || null })}>
              <option value="">— 未指定 —</option>
              {lookup?.staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          <Field label="發包人" span={3}>
            <select value={header.issuer_id ?? ''} onChange={(e) => setHeader({ ...header, issuer_id: Number(e.target.value) || null })}>
              <option value="">— 未指定 —</option>
              {lookup?.staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>

          <Field label="聯絡方式" span={3}>
            <input value={header.issuer_contact} onChange={(e) => setHeader({ ...header, issuer_contact: e.target.value })} />
          </Field>
          <Field label="發包日" span={3}>
            <input type="date" value={header.issued_on} onChange={(e) => setHeader({ ...header, issued_on: e.target.value })} />
          </Field>
          <Field label="承包確認日" span={3}>
            <input type="date" value={header.confirmed_on} onChange={(e) => setHeader({ ...header, confirmed_on: e.target.value })} />
          </Field>
          <Field label="約定交件日" span={3}>
            <input type="date" value={header.due_on} onChange={(e) => setHeader({ ...header, due_on: e.target.value })} />
          </Field>
          <Field label="語系" span={3}>
            <select value={header.lang} onChange={(e) => setHeader({ ...header, lang: e.target.value })}>
              <option value="英">英</option>
              <option value="中">中</option>
            </select>
          </Field>
          <Field label="幣別／稅別" span={3}>
            <select value={header.tax_mode} onChange={(e) => setHeader({ ...header, tax_mode: e.target.value })}>
              <option value="實際支付">實際支付</option>
              <option value="NTD／未稅">NTD／未稅</option>
              <option value="NTD／含稅">NTD／含稅</option>
            </select>
          </Field>
          <Field label="費率等級" span={3}>
            <select value={header.rate_level} onChange={(e) => setHeader({ ...header, rate_level: e.target.value })}>
              <option value="">— 未指定 —</option>
              {/* 「正常」已於 migrations/009 改名為「正常外包」，舊資料一併轉過 */}
              <option value="正常外包">正常外包</option>
              <option value="試寫">試寫</option>
              <option value="內包">內包</option>
            </select>
          </Field>

          <Field label="稿件交付連結" span={12}>
            <input value={header.deliver_link} onChange={(e) => setHeader({ ...header, deliver_link: e.target.value })} placeholder="Google Drive 資料夾連結" />
          </Field>
        </div>
      </Panel>

      <Panel
        title="二、發包明細（一張發包單可含多個客戶／合約，負責人可逐列指定）"
        actions={<button onClick={() => setItems([...items, blankItem(items.length + 1)])}>＋ 新增列</button>}
      >
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th style={{ width: 40 }}>項次</th>
                <th style={{ minWidth: 130 }}>客戶／專案</th>
                <th style={{ minWidth: 130 }}>合約編號</th>
                <th style={{ minWidth: 130 }}>服務項目</th>
                <th style={{ minWidth: 100 }}>負責人</th>
                <th className="num" style={{ width: 80 }}>件數</th>
                <th className="num" style={{ width: 100 }}>單價</th>
                <th className="num" style={{ width: 110 }}>小計</th>
                <th style={{ width: 50 }}></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => (
                <tr key={idx}>
                  <td>{idx + 1}</td>
                  <td>
                    <Combo
                      items={lookup?.clients ?? []}
                      value={it.client_id}
                      placeholder="輸入客戶"
                      allowEmpty={false}
                      sub={(c) => c.short_name ?? ''}
                      onChange={(c) => setItem(idx, { client_id: c ? c.id : 0 })}
                    />
                  </td>
                  <td>
                    <input
                      value={it.contract_code ?? ''}
                      placeholder="例：F114631加簽1"
                      onChange={(e) => setItem(idx, { contract_code: e.target.value })}
                    />
                  </td>
                  <td>
                    <select value={it.job_type_id ?? ''} onChange={(e) => setItem(idx, { job_type_id: Number(e.target.value) || null })}>
                      <option value="">— 未指定 —</option>
                      {lookup?.job_types.map((j) => <option key={j.id} value={j.id}>{j.name}</option>)}
                    </select>
                  </td>
                  <td>
                    <select value={it.owner_id ?? ''} onChange={(e) => setItem(idx, { owner_id: Number(e.target.value) || null })}>
                      <option value="">— 同表頭 —</option>
                      {lookup?.staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </td>
                  <td className="num">
                    <input type="number" min={1} value={it.qty} onChange={(e) => setItem(idx, { qty: Number(e.target.value) })} />
                  </td>
                  <td className="num">
                    <input type="number" min={0} step={100} value={it.unit_price} onChange={(e) => setItem(idx, { unit_price: Number(e.target.value) })} />
                  </td>
                  <td className="num">{money(it.subtotal)}</td>
                  <td>
                    <button className="danger" disabled={items.length === 1} onClick={() => setItems(items.filter((_, i) => i !== idx))}>刪</button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={5}>合計</td>
                <td className="num">{totals.qty}</td>
                <td></td>
                <td className="num">{money(totals.amount)}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Panel>

      <Panel
        title="三、文章需求明細"
        actions={<button onClick={() => setArticles([...articles, blankArticle(articles.length + 1)])}>＋ 新增列</button>}
      >
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th style={{ width: 40 }}>序號</th>
                <th style={{ minWidth: 120 }}>客戶</th>
                <th style={{ minWidth: 240 }}>文章主題（暫定標題）</th>
                <th style={{ minWidth: 160 }}>目標關鍵字</th>
                <th style={{ width: 140 }}>本篇交稿日</th>
                <th style={{ width: 50 }}></th>
              </tr>
            </thead>
            <tbody>
              {articles.map((a, idx) => (
                <tr key={idx}>
                  <td>{idx + 1}</td>
                  <td>
                    <Combo
                      items={lookup?.clients ?? []}
                      value={a.client_id}
                      placeholder="輸入客戶"
                      sub={(c) => c.short_name ?? ''}
                      onChange={(c) =>
                        setArticles(articles.map((x, i) =>
                          i === idx ? { ...x, client_id: c ? c.id : null } : x))
                      }
                    />
                  </td>
                  <td>
                    <input value={a.title ?? ''} onChange={(e) => setArticles(articles.map((x, i) => i === idx ? { ...x, title: e.target.value } : x))} />
                  </td>
                  <td>
                    <input value={a.keyword ?? ''} onChange={(e) => setArticles(articles.map((x, i) => i === idx ? { ...x, keyword: e.target.value } : x))} />
                  </td>
                  <td>
                    <input type="date" value={a.due_on ?? ''} onChange={(e) => setArticles(articles.map((x, i) => i === idx ? { ...x, due_on: e.target.value } : x))} />
                  </td>
                  <td>
                    <button className="danger" disabled={articles.length === 1} onClick={() => setArticles(articles.filter((_, i) => i !== idx))}>刪</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel title="備註">
        <textarea value={header.note} onChange={(e) => setHeader({ ...header, note: e.target.value })} />
      </Panel>

      <Panel
        title="注意事項"
        actions={
          <button
            className="chip"
            onClick={() => setHeader((h) => ({ ...h, terms: defaultTerms(h.dispatch_type) }))}
          >
            還原預設條款
          </button>
        }
      >
        <textarea
          style={{ minHeight: 140 }}
          value={header.terms}
          onChange={(e) => setHeader({ ...header, terms: e.target.value })}
        />
        <p className="chart-note">
          會原樣印在發包單上，一行一條，條號請自行編。切換發包類型時，
          內容若還是預設條款會自動換成新類型的版本；改過的內容不會被覆蓋。
        </p>
      </Panel>
      </fieldset>
    </>
  )
}
